
import React, { useState } from 'react';
import { X, Trash2, Plus, Minus, MessageCircle, User, Phone, Wallet, CreditCard, Truck } from 'lucide-react';
import { CartItem, PaymentMethod } from '../types';
import { formatPrice } from '../constants';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onRemove: (id: string, variantId?: string) => void;
  onUpdateQty: (id: string, variantId: string | undefined, delta: number) => void;
  onCheckout: (name: string, phone: string, payment: PaymentMethod) => void;
}

const CartSidebar: React.FC<CartSidebarProps> = ({ isOpen, onClose, items, onRemove, onUpdateQty, onCheckout }) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Orange Money');
  const [step, setStep] = useState<'cart' | 'info'>('cart');
  const total = items.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  const handleCheckoutClick = () => {
    if (step === 'cart') {
      setStep('info');
    } else {
      if (!customerName || !customerPhone) {
        alert("Veuillez remplir vos coordonnées pour continuer.");
        return;
      }
      onCheckout(customerName, customerPhone, paymentMethod);
      setStep('cart');
      setCustomerName('');
      setCustomerPhone('');
    }
  };

  const paymentOptions: { id: PaymentMethod; label: string; icon: any }[] = [
    { id: 'Wave', label: 'Wave', icon: CreditCard },
    { id: 'Paiement à la livraison', label: 'À la livraison', icon: Truck },
  ];

  return (
    <>
      <div 
        className={`fixed inset-0 bg-brand-black/30 backdrop-blur-sm z-[70] transition-opacity duration-500 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      <div className={`fixed top-0 right-0 h-full w-full max-w-md bg-brand-offwhite z-[80] shadow-2xl transition-transform duration-700 ease-[cubic-bezier(0.16, 1, 0.3, 1)] ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-brand-black/5 flex items-center justify-between">
            <h2 className="font-serif text-2xl text-brand-black">
              {step === 'cart' ? 'Votre Sélection' : 'Détails de Livraison'}
            </h2>
            <button onClick={onClose} className="p-2 hover:rotate-90 transition-transform duration-300 text-brand-black">
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-brand-nude/30">
            {step === 'cart' ? (
              <div className="space-y-8">
                {items.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-20">
                    <p className="text-brand-black/40 mb-6 font-light">Votre panier est encore vide.</p>
                    <button 
                      onClick={onClose}
                      className="text-[11px] uppercase tracking-widest font-bold text-brand-gold border-b border-brand-gold pb-1"
                    >
                      Explorer les collections
                    </button>
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <div key={`${item.id}-${item.selectedVariant?.id || 'none'}-${idx}`} className="flex space-x-4 animate-in fade-in slide-in-from-right-4 duration-300">
                      <div className="w-20 h-24 bg-brand-cream/50 rounded-lg overflow-hidden border border-brand-black/5">
                        <img src={item.selectedVariant?.image || item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="font-serif text-md text-brand-black">{item.name}</h3>
                            {item.selectedVariant && (
                              <p className="text-[9px] text-brand-gold uppercase tracking-[0.2em] font-bold">
                                {item.selectedVariant.name}
                              </p>
                            )}
                          </div>
                          <button onClick={() => onRemove(item.id, item.selectedVariant?.id)} className="text-brand-black/30 hover:text-red-500 luxury-transition">
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <p className="text-[10px] text-brand-black/50 uppercase tracking-widest mb-3 mt-1">
                          {formatPrice(item.price)}
                        </p>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center border border-brand-black/10 rounded-sm bg-brand-cream/20">
                            <button onClick={() => onUpdateQty(item.id, item.selectedVariant?.id, -1)} className="p-1.5 hover:bg-brand-black/5 text-brand-black"><Minus size={10} /></button>
                            <span className="text-[10px] w-8 text-center font-bold text-brand-black">{item.quantity}</span>
                            <button onClick={() => onUpdateQty(item.id, item.selectedVariant?.id, 1)} className="p-1.5 hover:bg-brand-black/5 text-brand-black"><Plus size={10} /></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-8 animate-in fade-in duration-500">
                <div className="space-y-4">
                   <p className="text-[10px] uppercase tracking-widest font-bold text-brand-gold">Informations Personnelles</p>
                   <div className="relative">
                     <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-black/40" />
                     <input 
                      type="text" 
                      placeholder="Nom complet" 
                      className="w-full p-4 pl-12 border border-brand-black/10 bg-brand-cream/20 rounded-xl outline-none focus:border-brand-gold text-sm text-brand-black placeholder:text-brand-black/30"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                     />
                   </div>
                   <div className="relative">
                     <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-black/40" />
                     <input 
                      type="tel" 
                      placeholder="Numéro WhatsApp" 
                      className="w-full p-4 pl-12 border border-brand-black/10 bg-brand-cream/20 rounded-xl outline-none focus:border-brand-gold text-sm text-brand-black placeholder:text-brand-black/30"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                     />
                   </div>
                </div>

                <div className="space-y-4">
                   <p className="text-[10px] uppercase tracking-widest font-bold text-brand-gold">Mode de Règlement</p>
                   <div className="grid grid-cols-1 gap-2">
                     {paymentOptions.map((opt) => (
                       <button
                         key={opt.id}
                         onClick={() => setPaymentMethod(opt.id)}
                         className={`flex items-center gap-4 p-4 border rounded-xl luxury-transition text-left ${
                           paymentMethod === opt.id 
                            ? 'border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold' 
                            : 'border-brand-black/5 hover:border-brand-gold/50 bg-brand-cream/10'
                         }`}
                       >
                         <opt.icon size={18} className={paymentMethod === opt.id ? 'text-brand-gold' : 'text-brand-black/40'} />
                         <span className={`text-xs uppercase tracking-widest font-bold ${paymentMethod === opt.id ? 'text-brand-black' : 'text-brand-black/50'}`}>
                           {opt.label}
                         </span>
                       </button>
                     ))}
                   </div>
                </div>

                <button 
                  onClick={() => setStep('cart')}
                  className="text-[9px] uppercase tracking-[0.2em] font-bold text-brand-black/40 hover:text-brand-gold"
                >
                  ← Modifier le panier
                </button>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="p-8 border-t border-brand-black/5 bg-brand-cream">
              <div className="flex items-center justify-between mb-8">
                <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-brand-black">Total Estimé</span>
                <span className="font-serif text-2xl text-brand-gold">
                  {formatPrice(total)}
                </span>
              </div>
              <button 
                onClick={handleCheckoutClick}
                className={`w-full ${step === 'cart' ? 'bg-brand-black' : 'bg-[#25D366]'} text-white py-5 text-[11px] uppercase tracking-[0.3em] font-bold hover:translate-y-[-2px] hover:shadow-xl transition-all flex items-center justify-center space-x-3 rounded-xl`}
              >
                {step === 'cart' ? (
                  <span>Valider la sélection</span>
                ) : (
                  <>
                    <MessageCircle size={18} />
                    <span>Envoyer la commande</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;
